import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, TriangleAlert } from 'lucide-react';
import { Loader } from '@strivelabs/ui-core/v2';
import { useAppStore } from '@/store/appStore';
import { getChatWorkflowInstanceId } from '@/utils/featureFlags';
import { useChatList } from '../../hooks/useChatList';
import { useChatActions } from '../../hooks/useChatActions';
import { ChatGroup } from './ChatGroup';
import { ChatSearch } from './ChatSearch';
import { RenameDialog } from './RenameDialog';
import { DeleteDialog } from './DeleteDialog';

function EmptyState({ isSearching }: { isSearching: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-v2-sidebar-foreground/70">
      <MessageSquare size={20} />
      <p className="text-pretty text-center text-v2-xs">
        {isSearching ? 'No chats match your search' : 'No conversations yet'}
      </p>
    </div>
  );
}

/**
 * Shown when the list request fails.
 *
 * Without this a failed fetch falls through to the empty state, which tells the
 * user they have no conversations — a claim the sidebar is in no position to
 * make when it never heard back.
 */
function ErrorState() {
  return (
    <div role="status" className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-v2-sidebar-foreground/70">
      <TriangleAlert size={20} />
      <p className="text-pretty text-center text-v2-xs">Could not load your chats</p>
    </div>
  );
}

/**
 * The Chat tab: search, conversations bucketed by relative date, and infinite
 * scroll. Rename and delete are reached from each row's menu.
 */
export function ChatTab() {
  const { executionId } = useParams<{ executionId?: string }>();
  const agentInstances = useAppStore(state => state.agentInstances);
  const workflowInstanceId = getChatWorkflowInstanceId(agentInstances) ?? '';

  const list = useChatList(workflowInstanceId);
  const actions = useChatActions(workflowInstanceId);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, error } = list;
  const hasRows = list.groups.length > 0;

  const onIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const node = sentinelRef.current;
    // Stop observing once a page has failed. `hasNextPage` still reads from the
    // last page that succeeded, so it stays true, and the sentinel stays in
    // view under a short list — re-observing would fetch, fail, and re-observe
    // on a loop with no retry delay behind it.
    if (!node || error) return;
    const observer = new IntersectionObserver(onIntersect, { rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [onIntersect, error]);

  // No chat agent for this workspace — the tab is still in the strip, so say why
  // rather than showing a blank panel.
  if (!workflowInstanceId) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-v2-sidebar-foreground/70">
        <MessageSquare size={20} />
        <p className="text-pretty text-center text-v2-xs">Chat isn&apos;t set up for this workspace yet</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <ChatSearch value={list.search} onChange={list.setSearch} />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {list.isLoading ? (
          <div role="status" aria-label="Loading chats" className="grid place-items-center py-12">
            <Loader />
          </div>
        ) : error && !hasRows ? (
          <ErrorState />
        ) : list.isEmpty ? (
          <EmptyState isSearching={list.isSearching} />
        ) : (
          <ul className="flex flex-col">
            {list.groups.map(({ group, executions }) => (
              <ChatGroup
                key={group}
                group={group}
                executions={executions}
                activeExecutionId={executionId}
                onRename={actions.startRename}
                onDelete={actions.startDelete}
              />
            ))}
          </ul>
        )}

        <div ref={sentinelRef} aria-hidden className="h-1" />
        {isFetchingNextPage ? (
          <div role="status" aria-label="Loading more chats" className="grid place-items-center py-3">
            <Loader />
          </div>
        ) : null}
        {/* A later page failing must not take the conversations already on
            screen with it — the query keeps its data through a failed fetch, so
            this reports the gap in place instead of replacing the list. */}
        {error && hasRows ? (
          <p role="status" className="text-pretty px-4 py-3 text-center text-v2-xs text-v2-sidebar-foreground/70">
            Could not load more chats
          </p>
        ) : null}
      </div>

      <RenameDialog
        execution={actions.renaming}
        isSaving={actions.isRenaming}
        onCancel={actions.cancelRename}
        onSave={actions.rename}
      />
      <DeleteDialog
        open={actions.deletingId !== null}
        isDeleting={actions.isDeleting}
        onCancel={actions.cancelDelete}
        onConfirm={actions.confirmDelete}
      />
    </div>
  );
}
